import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../../utils/authFetch/authFetch";
import ExplorePeoplePage from "./components/explorePeoplePage/explorePeoplePage";
import ExploreToggle from "./components/exploreToggle/exploreToggle";

import ActiveNotificationBanner from "../notifications/components/activeNotificationBanner/activeNotificationBanner";
import FilterSection from "./components/filterSection/filterSection";
import TodaySection from "./components/todaySection/todaySection";

import CardLeft from "./components/projectCard/CardLeft";
import CardMid from "./components/projectCard/CardMid";
import CardRight from "./components/projectCard/CardRight";
import SkeletonCard from "../../components/utilsComponents/SkeletonCard";
import VerifyEmailBanner from "./components/verifyEmailBanner/verifyEmailBanner";

import HeroImage from "../../assets/images/hero.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExplorePage = ({ collapsed, setCollapsed, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username: urlUsername } = useParams();
  const username = user?.username || urlUsername || localStorage.getItem("username");

  const [toastAlerts, setToastAlerts] = useState(true);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [matchedProjects, setMatchedProjects] = useState([]);
  const [otherProjects, setOtherProjects] = useState([]);
  const [userProjects, setUserProjects] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [activeNoti, setActiveNoti] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(user?.emailVerified || false);

  const [activeTab, setActiveTab] = useState("projects");

  const token = localStorage.getItem("token");

  useEffect(() => {
  if (user?.username) {
    localStorage.setItem("username", user.username);
  } else if (urlUsername && urlUsername !== "undefined") {
    localStorage.setItem("username", urlUsername);
  }
}, [user?.username, urlUsername]);


  useEffect(() => {
    const fetchToastPreference = async () => {
      if (!token) return;
      try {
        const res = await authFetch(`${BACKEND_URL}/projects/feed`, {}, navigate);

        const data = await res.json();
        if (res.ok && data.preferences) setToastAlerts(data.preferences.toastAlerts);
      } catch (err) {
        console.error("Error loading toast preference:", err);
      }
    };
    fetchToastPreference();
  }, [token]);

  useEffect(() => {
    const fetchVerificationStatus = async () => {
      if (!user?.email || !token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/user/${user.email}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && typeof data.emailVerified === "boolean") {
          setIsEmailVerified(data.emailVerified);
        }
      } catch (err) {
        console.error("Failed to fetch email verification status:", err);
      }
    };
    fetchVerificationStatus();
  }, [user?.email, token]);

  useEffect(() => {
    const syncEmailVerification = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data?.emailVerified !== undefined) {
          setIsEmailVerified(data.emailVerified);
        }
      } catch (err) {
        console.error("Failed to sync verification:", err);
      }
    };
    syncEmailVerification();
  }, [token]);

  useEffect(() => {
  let isMounted = true;
  let hasFetched = false;

  const fetchProjects = async () => {
    if (!user?.email || !token) return;
    console.log("🚀 Fetching project feed for:", user.email);
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/projects/feed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch project feed");
      const data = await res.json();

      if (!isMounted) return;
      setMatchedProjects(data.matchTags || []);
      setProjects(data.others || []);
      setUserProjects(data.userProjects || []);
      setAllProjects([...(data.userProjects || []), ...(data.others || [])]);
    } catch (err) {
      console.error("❌ Error fetching projects:", err);
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  // Wait until user+token are available
  let retries = 0;
  const retryInterval = setInterval(() => {
    if (user?.email && token && !hasFetched) {
      clearInterval(retryInterval);
      hasFetched = true;
      fetchProjects();
    } else {
      console.log("⏳ Waiting for user/token to load...");
    }
    if (retries++ > 20) clearInterval(retryInterval); // stop after ~10s
  }, 500);

  // periodic refresh
  const refresh = setInterval(() => {
    if (user?.email && token) fetchProjects();
  }, 180000);

  return () => {
    isMounted = false;
    clearInterval(retryInterval);
    clearInterval(refresh);
  };
}, [user?.email, token, location.pathname]);


  useEffect(() => {
  const handleFocus = () => {
    const fetchUpdatedProjects = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/projects/feed`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();

          // Always make sure these match the actual response keys from backend
          setMatchedProjects(data.matchTags || []);
          setProjects(data.others || []);
          setUserProjects(data.userProjects || []);

          // Update allProjects if needed for category filtering
          setAllProjects([...data.others || [], ...data.userProjects || []]);
        }
      } catch (err) {
        console.error("Error refreshing projects on focus:", err);
      }
    };

    fetchUpdatedProjects();
  };

  window.addEventListener("focus", handleFocus);
  return () => window.removeEventListener("focus", handleFocus);
}, [token, selectedCategory]);


  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        if (!token) return;
        const res = await fetch(`${BACKEND_URL}/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch notifications");
        const data = await res.json();
        const latest = data.notifications?.[0];
        if (latest && !latest.read) {
          const { applicantName, projectTitle, roleTitle } = latest;
          const msg = applicantName && projectTitle && roleTitle
            ? `📨 ${applicantName} applied to \"${projectTitle}\" for \"${roleTitle}\"`
            : latest.message || "📨 Someone applied to your project";

          setActiveNoti({ type: "application_submitted", message: msg });

          await fetch(`${BACKEND_URL}/notifications/mark-as-read`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };
    fetchNotifications();
  }, [token]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setLoading(true);
    setTimeout(() => {
      setProjects(category === "All"
        ? allProjects
        : allProjects.filter((proj) => proj.category?.toLowerCase() === category.toLowerCase()));
      setLoading(false);
    }, 300);
  };


  return (
    <div className="min-h-screen w-full p-4 md:p-6 lg:p-8 bg-neutral-100">
      {toastAlerts && activeNoti && (
        <ActiveNotificationBanner notification={activeNoti} onClose={() => setActiveNoti(null)} />
      )}

      {!isEmailVerified && (
        <div className="mb-4">
          <VerifyEmailBanner
            onResend={async () => {
              try {
                const res = await fetch(`${BACKEND_URL}/auth/resend-verification`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (!res.ok) {
                  if (data.message === "Email already verified.") {
                    alert("You're already verified!");
                    setIsEmailVerified(true);
                    return;
                  }
                  throw new Error(data.message || "Failed to send");
                }
                alert("Verification email sent!");
              } catch (err) {
                alert("Something went wrong. Try again.");
              }
            }}
          />
        </div>
      )}

      <div className="flex flex-col gap-2 mb-6">
        {/* Row: Title and Filter */}
        <div className="flex flex-row items-center gap-4 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Today I’m Exploring...
          </h1>

          {/* Explore Toggle Switch */}
          <ExploreToggle activeTab={activeTab} setActiveTab={setActiveTab} />
          
          {activeTab === "projects" && (
            <div className="flex flex-row items-center gap-2">
              <FilterSection
                selectedCategory={selectedCategory}
                onSelectCategory={handleCategorySelect}
              />
            <TodaySection />
          </div>
        )}
        </div>
      </div>


      {activeTab === "projects" && (
        <div className="w-full max-w-[1280px] mx-auto px-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
  {/* LEFT SIDE: Hero + Project Grid */}
  <div className="flex flex-col gap-6">
    {/* 🖼️ Hero */}
    <div className="w-full h-72 sm:h-72 rounded-[45px] overflow-hidden relative shadow-md bg-black/20">
      <img src={HeroImage} alt="Inspiring Project" className="w-full h-full object-cover" />
      <div className="absolute bottom-6 left-6 text-left space-y-1">
        <h2 className="text-white text-base sm:text-xl md:text-2xl font-semibold leading-snug font-inter">
          Your journey begins here.
        </h2>
        <div className="w-96 h-5 justify-start text-white text-[10px] font-normal font-['FONTSPRING_DEMO_-_Greycliff_CF']">
          Be the one who builds what others only dream of.
        </div>
      </div>
    </div>

    {/* 🔹 Grid: Top 3 Projects on LEFT (CardLeft), others split into Mid & Right */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* 🔸 LEFT COLUMN - Top 5 Matches */}
      <div className="flex flex-col gap-4">
        {matchedProjects.slice(0, 5).map((project) => (
          <CardLeft
            key={project.id}
            project={{
              ...project,
              fireCount: project.fireCount || 0,
              image: `${project.image}?v=${project.updatedAt || Date.now()}`,
            }}
            onClick={() => navigateTo(project)}
            user={user}
          />
        ))}
      </div>

      {/* 🔸 MID COLUMN - Other Projects (even index) */}
      <div className="flex flex-col gap-4">
        {[
  ...userProjects,
  ...projects.filter((_, i) => i % 2 === 0),
].map((project) => (
  <CardMid
    key={project.id}
    project={{
      ...project,
      fireCount: project.fireCount || 0,
      image: `${project.image}?v=${project.updatedAt || Date.now()}`,
    }}
    onClick={() => navigateTo(project)}
    user={user}
  />
))}
      </div>
    </div>
  </div>

  {/* 🔸 RIGHT COLUMN - Other Projects (odd index) */}
  <div className="flex flex-col gap-4 mt-0 lg:mt-0">
    {projects.filter((_, i) => i % 2 === 1).map((project) => (
      <CardRight
        key={project.id}
        project={{
          ...project,
          fireCount: project.fireCount || 0,
          image: `${project.image}?v=${project.updatedAt || Date.now()}`,
        }}
        onClick={() => navigateTo(project)}
        user={user}
      />
    ))}
  </div>
</div>
      )}

      {activeTab === "people" && (
        <ExplorePeoplePage user={user} />
      )}

    </div>
  );

  function navigateTo(project) {
    fetch(`${BACKEND_URL}/projects/${project.id}/admin-view`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    }).then(async (res) => {
      const data = await res.json();
      if (res.ok && data.isAdmin) {
        navigate(`/projects/${project.id}/admin-view`);
      } else {
        navigate(`/projects/${project.id}/details`);
      }
    }).catch(() => navigate(`/projects/${project.id}/details`));
  }
};

export default ExplorePage;
